"use client";

import { useRef, useState, useEffect } from "react";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { IconButton, useMediaQuery } from "@mui/material";
import { useTranslations } from "next-intl";
import { Variant } from "@/hooks/useVariant";
import { SymptomCardConfig } from "@/api/types";
import { getAssetUrl } from "@/utils/assetUrl";
import CardComponent from "./CardComponent/CardComponent";

function getImageUrl(imageName: string, variant: Variant): string {
  return getAssetUrl(`/images/results/${imageName}_${variant}.webp`);
}

interface SymptomResultsSectionProps {
  variant: Variant;
  userName: string;
  symptomCards: SymptomCardConfig[];
}

export default function SymptomResultsSection({
  variant,
  userName,
  symptomCards,
}: SymptomResultsSectionProps) {
  const t = useTranslations("results-page.symptom-results-section");
  const tAria = useTranslations("aria");

  // Resolve the expanded description key for a card, preferring variant-specific
  // content when available
  const getExpandedDescriptionKey = (card: SymptomCardConfig): string => {
    const variantKey = `${card.id}.${variant}.expanded-description`;
    return t.has(variantKey) ? variantKey : card.expandedDescriptionKey;
  };

  const totalCards = symptomCards.length;

  const isMobile = useMediaQuery("(max-width:767px)");
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );

  const gridRef = useRef<HTMLDivElement>(null);
  const indicatorRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  /** Scroll the grid so the card at `index` is centered. */
  const scrollToCard = (index: number) => {
    const clampedIndex = Math.max(0, Math.min(totalCards - 1, index));
    setActiveIndex(clampedIndex);

    const grid = gridRef.current;
    const card = grid?.children[clampedIndex] as HTMLElement | undefined;
    if (!grid || !card) return;

    // Calculate where to scroll to so the focused card appears centered
    const scrollLeft =
      card.offsetLeft - (grid.clientWidth - card.offsetWidth) / 2;
    grid.scrollTo({
      left: scrollLeft,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  };

  /** Keep activeIndex in sync with scroll position (center-snap aware). */
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid || !isMobile) return;

    const handleScroll = () => {
      const children = Array.from(grid.children) as HTMLElement[];
      if (children.length === 0) return;

      // Helpers for scrolling logic below
      const gridCenter = grid.scrollLeft + grid.clientWidth / 2;
      let closest = 0;
      let minDist = Infinity;

      // Determine which card is closest to the middle of the screen
      children.forEach((child, i) => {
        const childCenter = child.offsetLeft + child.offsetWidth / 2;
        const dist = Math.abs(childCenter - gridCenter);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      setActiveIndex(closest);
    };

    grid.addEventListener("scroll", handleScroll, { passive: true });
    return () => grid.removeEventListener("scroll", handleScroll);
  }, [isMobile]);

  /** Roving tabindex keyboard navigation for indicator dots. */
  const handleIndicatorKeyDown = (
    event: React.KeyboardEvent,
    index: number,
  ) => {
    // Map key presses to actions
    const keyMap: Record<string, number | undefined> = {
      ArrowLeft: index > 0 ? index - 1 : undefined,
      ArrowRight: index < totalCards - 1 ? index + 1 : undefined,
      Home: 0,
      End: totalCards - 1,
    };

    // Determine what key was pressed and if we should ignore it
    const newIndex = keyMap[event.key];
    if (newIndex === undefined || newIndex === index) return;

    // Scroll based on the users key press
    event.preventDefault();
    scrollToCard(newIndex);
    indicatorRefs.current[newIndex]?.focus();
  };

  return (
    <section
      className="symptom-results-section"
      role="region"
      aria-label={tAria("symptom-carousel")}
      {...(isMobile && { "aria-roledescription": "carousel" })}
    >
      <div className="symptom-results-section-header">
        <h2
          className={`headline symptom-results-section-heading symptom-results-section-heading--${variant}`}
        >
          {t("heading", { name: userName })}
        </h2>
        <p
          className={`symptom-results-section-subtitle symptom-results-section-subtitle--${variant}`}
        >
          {t("subtitle")}
        </p>
      </div>

      <div
        className="symptom-results-section-grid"
        ref={gridRef}
        aria-live="polite"
      >
        {symptomCards.map((card) => (
          <CardComponent
            key={card.id}
            cardId={card.id}
            variant={variant}
            riskLabelKey={card.riskLabelKey}
            titleKey={card.titleKey}
            descriptionIntroKey={card.descriptionIntroKey}
            imageUrl={getImageUrl(card.imageUrl, variant)}
            ctaLabelKey={card.ctaLabelKey}
            expandedDescription={
              <p>
                {t.rich(getExpandedDescriptionKey(card), {
                  br: () => <br />,
                  sup: (chunks) => <sup>{chunks}</sup>,
                })}
              </p>
            }
            treatmentOptionsLabelKey={card.treatmentOptionsLabelKey}
            explainabilityBullets={card.explainabilityBullets}
          />
        ))}
      </div>

      {/* Mobile carousel controls */}
      {isMobile && (
        <div className="symptom-results-section-controls">
          <IconButton
            className="symptom-results-section-arrow"
            onClick={() => scrollToCard(activeIndex - 1)}
            disabled={activeIndex === 0}
            aria-label={tAria("previous-card")}
          >
            <ArrowBack aria-hidden focusable={false} />
          </IconButton>

          <div
            className="symptom-results-section-indicators"
            role="group"
            aria-label={tAria("card-indicators")}
          >
            {symptomCards.map((card, i) => (
              <button
                key={card.id}
                ref={(button) => {
                  indicatorRefs.current[i] = button;
                }}
                type="button"
                className={`symptom-results-section-dot${i === activeIndex ? " symptom-results-section-dot--active" : ""}`}
                aria-current={i === activeIndex || undefined}
                aria-label={t(card.titleKey)}
                tabIndex={i === activeIndex ? 0 : -1}
                onClick={() => scrollToCard(i)}
                onKeyDown={(e) => handleIndicatorKeyDown(e, i)}
              />
            ))}
          </div>

          <IconButton
            className="symptom-results-section-arrow"
            onClick={() => scrollToCard(activeIndex + 1)}
            disabled={activeIndex === totalCards - 1}
            aria-label={tAria("next-card")}
          >
            <ArrowForward aria-hidden focusable={false} />
          </IconButton>
        </div>
      )}
    </section>
  );
}
